<template>
  <div
    v-if="hasHints"
    class="quest-chapter-hints">
    <div class="quest-chapter-hints__toggles">
      <div
        v-for="index in hintIndices"
        :key="index"
        class="quest-chapter-hints__toggle-container"
        :class="{
          'quest-chapter-hints__toggle-container--hidden': openHints[index]
        }">
        <button
          type="button"
          v-if="!openHints[index]"
          class="quest-chapter-hints__toggle"
          :class="{
            'quest-chapter-hints__toggle--disabled': isHintDisabled(index)
          }"
          :aria-expanded="Boolean(openHints[index])"
          :aria-controls="`hint-${index}`"
          @click="handleHintToggle(index)">
          <span>
            <LockIcon
              v-if="!isHintUsed(index)"
              class="quest-chapter-hints__toggle-icon w-12 h-12" />
            Hint <sup>{{ index + 1 }}</sup>
          </span>
        </button>
      </div>
    </div>

    <div class="quest-chapter-hints__list">
      <div
        v-for="index in hintIndices"
        :key="index"
        class="quest-chapter-hints__item"
        :class="{
          'quest-chapter-hints__item--active': openHints[index]
        }">
        <div
          v-if="openHints[index]"
          class="quest-chapter-hints__status"
          @click="handleHintToggle(index)">
          <CloseIcon class="quest-chapter-hints__close-icon w-20 h-20" />
        </div>

        <div
          v-if="openHints[index]"
          :id="`hint-${index}`"
          class="quest-chapter-hints__content">
          <h3 class="quest-chapter-hints__item-header">
            Hint <sup>{{ index + 1 }}</sup>
          </h3>

          <div
            v-if="hintContents[index]"
            v-html="$options.filters.mdWithP(hintContents[index])" />
          <div v-else>
            Loading hint...
          </div>
        </div>
      </div>
    </div>

    <ConfirmationPopup
      v-if="confirmationPopupShown"
      :copy="confirmationPopupCopy"
      @submit="handleConfirmationPopup" />
  </div>
</template>

<script>
import axios from "axios";
import ConfirmationPopup from "components/quest/confirmation-popup";
import { highlightAll } from "modules/code-highlight";
import { loadMathjax } from "controllers/mathjax";
import { notify } from "modules/notify";
import LockIcon from "@images/font_awesome/lock-alt-light.svg";
import CloseIcon from "@images/font_awesome/x-light.svg";
import Sentry from "modules/sentry";
import sharedStore from "components/quest/shared-store";

export default {
  name: "QuestChapterHints",
  components: {
    ConfirmationPopup,
    LockIcon,
    CloseIcon
  },
  props: {
    chapterId: {
      type: Number,
      required: true
    },
    hintCount: {
      type: Number,
      default: 0
    },
    usedHints: {
      type: Array,
      default: () => []
    },
    usedHintIndices: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      openHints: [],
      hintContents: [],
      localUsedHintIndices: [],
      confirmationPopupShown: false,
      confirmationPopupCopy: null,
      confirmationPopupAction: null,
      pendingHintRequests: {}
    }
  },
  computed: {
    hasHints() {
      return this.hintCount > 0;
    },
    hintIndices() {
      return Array.from({ length: this.hintCount }, (_, index) => index);
    }
  },
  watch: {
    hintCount: {
      immediate: true,
      handler(newCount) {
        this.resetHintState(newCount);
      }
    },
    usedHints: {
      immediate: true,
      handler(newUsedHints) {
        this.cacheUsedHintContents(newUsedHints);
      }
    },
    usedHintIndices: {
      immediate: true,
      handler(newIndices) {
        this.localUsedHintIndices = [...newIndices].sort((a, b) => a - b);
      }
    }
  },
  methods: {
    hintPenaltySec(index) {
      return (index + 1) * 5 * 60;
    },
    isHintDisabled(index) {
      const prevIndex = index - 1;

      if (prevIndex < 0) {
        return false;
      }

      if (this.openHints[index]) {
        return false;
      }

      return !this.isHintUsed(prevIndex);
    },
    resetHintState(count) {
      const length = Number(count) || 0;
      this.openHints = Array.from({ length }, () => false);
      this.ensureHintContentsLength(length);
    },
    ensureHintContentsLength(length) {
      const normalizedLength = Number(length) || 0;

      if (this.hintContents.length === normalizedLength) {
        return;
      }

      const nextContents = Array.from({ length: normalizedLength }, (_, index) => this.hintContents[index] || null);
      this.hintContents = nextContents;
    },
    cacheUsedHintContents(usedHints) {
      if (!Array.isArray(usedHints)) {
        return;
      }

      this.ensureHintContentsLength(this.hintCount);

      usedHints.forEach((hint) => {
        if (!hint || typeof hint.index !== "number") {
          return;
        }

        this.$set(this.hintContents, hint.index, hint.content || "");
      });
    },
    handleHintToggle(index) {
      if (!this.hasHints) {
        return;
      }

      if (this.openHints.length !== this.hintCount) {
        this.resetHintState(this.hintCount);
      }

      if (this.openHints[index]) {
        this.$set(this.openHints, index, false);
        return;
      }

      if (this.isHintUsed(index)) {
        this.openHint(index);
        return;
      }

      const hintPenaltySec = this.hintPenaltySec(index);
      const hintPenaltyMin = Math.floor(hintPenaltySec / 60);

      this.confirmationPopupCopy = `Are you sure you want to open this hint? It'll add a ${hintPenaltyMin} minute penalty to your time.`;
      this.confirmationPopupAction = { type: "open-hint", index };
      this.confirmationPopupShown = true;
    },
    openHint(index) {
      if (!this.hasHints) {
        return;
      }

      if (this.openHints.length !== this.hintCount) {
        this.resetHintState(this.hintCount);
      }

      if (this.openHints[index]) {
        return;
      }

      const showHint = () => {
        this.$set(this.openHints, index, true);
        this.$emit('hint-opened', index);

        this.$nextTick(() => {
          highlightAll();
          loadMathjax();
        });
      };

      if (this.hintContents[index]) {
        showHint();
        return;
      }

      this.requestHintContent(index)
        .then(() => {
          showHint();
        })
        .catch((error) => {
        });
    },
    isHintUsed(index) {
      return this.localUsedHintIndices.includes(index);
    },
    requestHintContent(index) {
      if (this.hintContents[index]) {
        return Promise.resolve(this.hintContents[index]);
      }

      if (this.pendingHintRequests[index]) {
        return this.pendingHintRequests[index];
      }

      const previousUsedIndices = [...this.localUsedHintIndices];
      const csrfTokenElement = document.querySelector("meta[name=csrf-token]");
      const csrfToken = csrfTokenElement ? csrfTokenElement.content : null;

      if (!this.isHintUsed(index)) {
        this.localUsedHintIndices = [...new Set([...this.localUsedHintIndices, index])].sort((a, b) => a - b);
      }

      const request = axios.patch(`/api/quests/chapters/${this.chapterId}/use_hint`, {
        hint_index: index
      }, {
        headers: {
          "X-CSRF-Token": csrfToken
        }
      }).then(response => {
        const data = response.data || {};

        if (Array.isArray(data.used_hint_indices)) {
          this.localUsedHintIndices = [...data.used_hint_indices].sort((a, b) => a - b);
        }

        if (data.hint && typeof data.hint.index === "number") {
          this.$set(this.hintContents, data.hint.index, data.hint.content || "");

          // Add the penalty to the shared store
          if (data.hint.penalty_sec) {
            sharedStore.addHintPenalty(data.hint.penalty_sec);
          }
        }

        return this.hintContents[index];
      }).catch(error => {
        console.error(error);
        this.localUsedHintIndices = previousUsedIndices;

        notify({
          message: "We couldn't record that hint usage. Please try again or reaach out to support@sqlhabit.com",
          type: "error"
        });

        Sentry.captureException(error);
      }).finally(() => {
        delete this.pendingHintRequests[index];
      });

      this.pendingHintRequests[index] = request;

      return request;
    },
    handleConfirmationPopup(confirm) {
      const action = this.confirmationPopupAction;

      this.confirmationPopupShown = false;
      this.confirmationPopupAction = null;

      if (!confirm || !action) {
        return;
      }

      if (action.type === "open-hint") {
        this.openHint(action.index);
      }
    }
  }
}
</script>

<style lang="scss">
.quest-chapter-hints {
  margin-bottom: $px32;

  &__list {
    display: flex;
    flex-direction: column;
    gap: $px12;
    margin-bottom: $px32;
  }

  &__item {
    display: none;
    position: relative;
    margin-bottom: $px12;

    border: $px1 solid $purple-7;
    border-radius: $px12;

    background-color: $grey-10;

    &--active {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
  }

  &__item-header {
    margin-top: 0;
    margin-bottom: $px16;

    @include h4;
  }

  &__toggles {
    display: flex;
    gap: $px12;
    margin-bottom: $px24;
  }

  &__toggle-container {
    &--hidden {
      display: none;
    }
  }

  &__toggle {
    display: flex;
    align-items: center;
    gap: $px12;
    padding: $px8 $px16;

    border: 0;
    border-radius: $px32;
    outline: none;
    appearance: none;

    background-color: $purple-9;

    @include font-small;
    text-transform: uppercase;

    cursor: pointer;

    &--disabled {
      background-color: $grey-9;

      opacity: 0.6;
      pointer-events: none;
      cursor: not-allowed;
    }
  }

  &__toggle:hover {
    background-color: $purple-8;
  }

  &__status {
    position: absolute;
    top: $px8;
    right: $px8;
    z-index: 10;

    @include font-small;

    cursor: pointer;

    &:hover {
      opacity: 0.7;

      cursor: pointer;
    }
  }

  &__content {
    padding: $px24 $px16 $px12;
  }
}
</style>
